import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, User, AlertCircle, Shield, ShieldAlert, KeyRound, Eye, EyeOff, Fingerprint, Building2, Clock, Users, Sparkles, ScanLine } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StaffLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [securityCode, setSecurityCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [securityPassed, setSecurityPassed] = useState(false);
  const [securityError, setSecurityError] = useState('');
  const [securityLoading, setSecurityLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [securityAttemptsLeft, setSecurityAttemptsLeft] = useState(null);
  const [securityLockout, setSecurityLockout] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [newPasswordInput, setNewPasswordInput] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [twoFACode, setTwoFACode] = useState(['', '', '', '', '', '']);
  const [processing2FA, setProcessing2FA] = useState(false);

  // 2FA protection state
  const [twoFAAttempts, setTwoFAAttempts] = useState(0);            // failed attempts counter
  const [twoFAIdleSec, setTwoFAIdleSec] = useState(60);             // idle auto-clear countdown (sec)
  const [twoFATotpSec, setTwoFATotpSec] = useState(30);             // TOTP window countdown
  const [twoFAUsedPaste, setTwoFAUsedPaste] = useState(false);      // flag if pasted
  const [twoFAHoneypot, setTwoFAHoneypot] = useState('');           // hidden anti-bot
  const [twoFAUsername2, setTwoFAUsername2] = useState('');         // mirror of username for display
  const [twoFAShake, setTwoFAShake] = useState(false);              // visual shake on error
  const [twoFASuccess, setTwoFASuccess] = useState(false);          // green pulse on success
  const [twoFALocked, setTwoFALocked] = useState(false);            // 5 fails → soft lock
  
  // Animation states
  const [codeDigits, setCodeDigits] = useState(['', '', '', '', '', '', '', '']);

  // Safety/protection state
  const [honeypot, setHoneypot] = useState('');           // hidden anti-bot field
  const [pageLoadTs] = useState(Date.now());              // for elapsed_ms bot signal
  const [pasteWarning, setPasteWarning] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [idleSecondsLeft, setIdleSecondsLeft] = useState(60);
  const [revealCode, setRevealCode] = useState(false); // brief peek toggle
  const [sessionFp] = useState(() => {
    // Short browser fingerprint hash for visual trust badge (NOT used for auth)
    const raw = `${navigator.userAgent}|${navigator.language}|${screen.width}x${screen.height}|${new Date().getTimezoneOffset()}`;
    let h = 0;
    for (let i = 0; i < raw.length; i++) { h = ((h << 5) - h) + raw.charCodeAt(i); h |= 0; }
    return Math.abs(h).toString(16).slice(0, 8).toUpperCase();
  });

  const navigate = useNavigate();
  const location = useLocation();

  // Update time every second & track lockout countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
      if (securityLockout && Date.now() >= securityLockout) {
        setSecurityLockout(null);
        setSecurityError('');
        setSecurityAttemptsLeft(null);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [securityLockout]);

  // ───── Auto-clear code on tab blur (security best practice) ─────
  useEffect(() => {
    if (securityPassed) return;
    const onVisibility = () => {
      if (document.hidden && codeDigits.some(d => d)) {
        setCodeDigits(['', '', '', '', '', '', '', '']);
        setSecurityCode('');
        setSecurityError('Code cleared — for your safety, codes are wiped when you switch tabs.');
        setIdleSecondsLeft(60);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [codeDigits, securityPassed]);

  // ───── 60-second idle timer — auto-clears code if no input ─────
  useEffect(() => {
    if (securityPassed || !codeDigits.some(d => d)) {
      setIdleSecondsLeft(60);
      return;
    }
    const tick = setInterval(() => {
      setIdleSecondsLeft(s => {
        if (s <= 1) {
          setCodeDigits(['', '', '', '', '', '', '', '']);
          setSecurityCode('');
          setSecurityError('Session timeout — code cleared after 60s of inactivity.');
          return 60;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [codeDigits, securityPassed]);

  // ───── Caps Lock detection ─────
  useEffect(() => {
    const onKey = (e) => {
      if (typeof e.getModifierState === 'function') {
        setCapsLockOn(e.getModifierState('CapsLock'));
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKey);
    };
  }, []);

  // No longer need Google OAuth callback handling for 2FA

  // No longer need pending 2FA session check

  // 2FA timers — TOTP 30s window + idle auto-clear
  useEffect(() => {
    if (!requires2FA) return;
    const totp = setInterval(() => {
      setTwoFATotpSec(s => (s <= 1 ? 30 : s - 1));
    }, 1000);
    const idle = setInterval(() => {
      setTwoFAIdleSec(s => {
        if (s <= 1) {
          // auto-clear code on idle
          setTwoFACode(['', '', '', '', '', '']);
          return 60;
        }
        return s - 1;
      });
    }, 1000);
    return () => { clearInterval(totp); clearInterval(idle); };
  }, [requires2FA]);

  // Auto-submit when 2FA code is fully entered
  useEffect(() => {
    if (requires2FA && !processing2FA && !twoFALocked && twoFACode.every(d => d !== '')) {
      const t = setTimeout(() => handle2FAVerification(), 200);
      return () => clearTimeout(t);
    }
  }, [twoFACode, requires2FA, processing2FA, twoFALocked]);



  // Handle security code input as individual digits
  const handleCodeChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    const newDigits = [...codeDigits];
    newDigits[index] = value.toUpperCase();
    setCodeDigits(newDigits);
    setSecurityCode(newDigits.join(''));
    setIdleSecondsLeft(60); // reset idle timer on input

    // Auto-focus next input
    if (value && index < 7) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      if (nextInput) nextInput.focus();
    }

    // Auto-submit when all 8 digits are filled — saves users from
    // hunting for the button and stops them submitting a short code.
    if (value && index === 7 && newDigits.every(d => d)) {
      setTimeout(() => {
        const formEl = document.querySelector('form[data-vault-form="1"]') || document.getElementById('code-7')?.closest('form');
        if (formEl && !securityLockout && !securityLoading) {
          formEl.requestSubmit ? formEl.requestSubmit() : formEl.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
      }, 120);
    }
  };

  // ───── Paste handler with bulk-fill + warning ─────
  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData?.getData('text') || '').replace(/\s/g, '').toUpperCase().slice(0, 8);
    if (!pasted) return;
    const newDigits = ['', '', '', '', '', '', '', ''];
    for (let i = 0; i < pasted.length && i < 8; i++) newDigits[i] = pasted[i];
    setCodeDigits(newDigits);
    setSecurityCode(newDigits.join(''));
    setPasteWarning(true);
    setTimeout(() => setPasteWarning(false), 4000);
    // Focus next empty box, or the last
    const nextIdx = Math.min(pasted.length, 7);
    setTimeout(() => {
      const el = document.getElementById(`code-${nextIdx}`);
      if (el) el.focus();
    }, 50);
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !codeDigits[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleSecurityCheck = async (e) => {
    e.preventDefault();
    setSecurityError('');
    setSecurityLoading(true);

    const fullCode = codeDigits.join('');
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/security-code/verify`, {
        code: fullCode,
        hp: honeypot,
        fp: sessionFp,
        elapsed_ms: Date.now() - pageLoadTs
      });
      if (response.data.valid) {
        setSecurityPassed(true);
        setSecurityAttemptsLeft(null);
        setSecurityLockout(null);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Verification failed';
      setSecurityError(detail);
      setCodeDigits(['', '', '', '', '', '', '', '']);
      setSecurityCode('');
      
      // Parse attempts remaining from error
      const attemptsMatch = detail.match(/(\d+)\s*attempt/);
      if (attemptsMatch) setSecurityAttemptsLeft(parseInt(attemptsMatch[1]));
      
      // Check for lockout
      if (err.response?.status === 429) {
        const timeMatch = detail.match(/(\d+)m\s*(\d+)s/);
        if (timeMatch) {
          const lockoutEnd = Date.now() + (parseInt(timeMatch[1]) * 60 + parseInt(timeMatch[2])) * 1000;
          setSecurityLockout(lockoutEnd);
        }
      }
      
      // Focus first input
      setTimeout(() => {
        const firstInput = document.getElementById('code-0');
        if (firstInput) firstInput.focus();
      }, 100);
    } finally {
      setSecurityLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/login`, {
        username,
        password
      });

      if (response.data.requires_2fa) {
        setRequires2FA(true);
        setTempToken(response.data.temp_token);
        setTwoFAUsername2(username);
        setTwoFAAttempts(0);
        setTwoFAIdleSec(60);
        setTwoFATotpSec(30);
        setTwoFAUsedPaste(false);
        setTwoFALocked(false);
      } else {
        localStorage.setItem('staff_token', response.data.access_token);
        if (response.data.must_change_password) {
          setMustChangePassword(true);
        } else {
          clearSessionStorage();
          navigate('/staff/dashboard');
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.detail || err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerification = async (e) => {
    if (e) e.preventDefault();
    const code = twoFACode.join('');
    if (code.length !== 6) return;
    if (twoFALocked) return;
    // Honeypot check
    if (twoFAHoneypot) {
      setError('Suspicious activity detected.');
      return;
    }
    
    setProcessing2FA(true);
    setError('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/2fa/verify-login?temp_token=${encodeURIComponent(tempToken)}`, {
        code
      });

      if (response.data.access_token) {
        setTwoFASuccess(true);
        localStorage.setItem('staff_token', response.data.access_token);
        setTimeout(() => navigate('/staff/dashboard'), 600);
      }
    } catch (err) {
      console.error('2FA verification error:', err);
      const newAttempts = twoFAAttempts + 1;
      setTwoFAAttempts(newAttempts);
      setTwoFAShake(true);
      setTimeout(() => setTwoFAShake(false), 500);
      if (newAttempts >= 5) {
        setTwoFALocked(true);
        setError('Too many failed attempts. Please cancel and sign in again.');
      } else {
        setError(err.response?.data?.detail || `Invalid code. ${5 - newAttempts} attempt${5 - newAttempts !== 1 ? 's' : ''} remaining.`);
      }
      setTwoFACode(['', '', '', '', '', '']);
      setTimeout(() => {
        const firstInput = document.getElementById('2fa-0');
        if (firstInput) firstInput.focus();
      }, 100);
    } finally {
      setProcessing2FA(false);
    }
  };

  const handle2FACodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    if (value.length > 1) value = value.slice(-1);
    const newCode = [...twoFACode];
    newCode[index] = value;
    setTwoFACode(newCode);
    setTwoFAIdleSec(60); // reset idle on input
    
    if (value && index < 5) {
      const nextInput = document.getElementById(`2fa-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handle2FAKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !twoFACode[index] && index > 0) {
      const prevInput = document.getElementById(`2fa-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handle2FAPaste = (e) => {
    e.preventDefault();
    setTwoFAUsedPaste(true);
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newCode = [...twoFACode];
      for (let i = 0; i < 6; i++) {
        newCode[i] = pasted[i] || '';
      }
      setTwoFACode(newCode);
      setTwoFAIdleSec(60);
      const focusIndex = Math.min(pasted.length, 5);
      const input = document.getElementById(`2fa-${focusIndex}`);
      if (input) input.focus();
    }
  };

  const clearSessionStorage = () => {
    // No longer storing 2FA data in sessionStorage
  };

  const handleForcePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    if (newPasswordInput.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (newPasswordInput !== confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }
    setChangingPassword(true);
    try {
      const token = localStorage.getItem('staff_token');
      await axios.post(`${BACKEND_URL}/api/admin/change-password-forced`, { new_password: newPasswordInput }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMustChangePassword(false);
      clearSessionStorage();
      navigate('/staff/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };


  const cancel2FA = () => {
    setRequires2FA(false);
    setTempToken('');
    setTwoFACode(['', '', '', '', '', '']);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  };

  // Security Gate Screen — Vault redesign with masked entry
  if (!securityPassed) {
    const filledCount = codeDigits.filter(d => d).length;
    return (
      <div className="min-h-screen bg-[#05050A] flex items-center justify-center p-4 sm:p-6 relative overflow-hidden" data-testid="security-gate">
        {/* Ambient vault atmosphere */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-[radial-gradient(circle,rgba(244,63,94,0.10)_0%,rgba(5,5,10,0)_60%)]" />
          <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-rose-500/[0.06] rounded-full blur-[120px] animate-pulse" />
          <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] bg-orange-500/[0.05] rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1.4s' }} />
          <div className="absolute inset-0 opacity-[0.025]" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.18) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }} />
          {/* Vertical scan line */}
          <div className="absolute inset-y-0 left-1/2 w-px bg-gradient-to-b from-transparent via-rose-400/20 to-transparent" />
        </div>

        <div className="relative z-10 w-full max-w-[440px]">
          {/* Brand row */}
          <div className="flex items-center justify-center gap-2.5 mb-6">
            <div className="w-9 h-9 bg-gradient-to-br from-rose-500 to-orange-600 rounded-lg flex items-center justify-center shadow-lg shadow-rose-500/30 ring-1 ring-white/10">
              <Building2 className="w-4 h-4 text-white" />
            </div>
            <div className="text-left">
              <p className="text-[9px] tracking-[0.32em] uppercase text-rose-300/50 font-medium leading-none">Starnight Holdings</p>
              <p className="text-sm font-semibold text-white/90 tracking-tight mt-0.5">Staff Hub · Restricted</p>
            </div>
          </div>

          {/* Vault card */}
          <div className="relative rounded-[22px] p-7 sm:p-8 backdrop-blur-2xl bg-[#0a0a14]/70 border border-rose-500/15 shadow-[0_20px_60px_rgba(0,0,0,0.55)]">
            {/* Rotating outer ring accent */}
            <div className="absolute -top-px inset-x-8 h-px bg-gradient-to-r from-transparent via-rose-400/60 to-transparent" />
            <div className="absolute -bottom-px inset-x-8 h-px bg-gradient-to-r from-transparent via-orange-400/30 to-transparent" />
            <div className="pointer-events-none absolute inset-0 rounded-[22px] ring-1 ring-inset ring-white/[0.03]" />

            {/* Vault dial header */}
            <div className="flex flex-col items-center text-center mb-7">
              <div className="relative w-20 h-20 mb-5">
                {/* Outer slow rotating dashed ring */}
                <div
                  className="absolute inset-0 rounded-full border-2 border-dashed border-rose-500/25"
                  style={{ animation: 'spin 22s linear infinite' }}
                />
                {/* Inner counter-rotating ring */}
                <div
                  className="absolute inset-2 rounded-full border border-orange-400/25"
                  style={{ animation: 'spin 14s linear infinite reverse' }}
                />
                {/* Center shield */}
                <div className="absolute inset-3 rounded-full bg-gradient-to-br from-rose-500/25 via-rose-500/10 to-orange-500/20 border border-rose-400/40 flex items-center justify-center shadow-[inset_0_0_20px_rgba(244,63,94,0.15)]">
                  <Shield className="w-7 h-7 text-rose-300 drop-shadow-[0_0_8px_rgba(244,63,94,0.55)]" strokeWidth={1.5} />
                </div>
                {/* Pulse dot */}
                <span className="absolute -top-0.5 right-2 w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.7)] animate-pulse" />
              </div>

              <p className="text-[10px] tracking-[0.35em] uppercase text-rose-300/60 mb-1.5 font-medium">Vault Checkpoint</p>
              <h2 className="text-2xl sm:text-[26px] font-semibold text-white tracking-tight leading-tight">
                Identify yourself
              </h2>
              <p className="text-slate-500 text-[13px] mt-1.5 max-w-xs">
                Enter your private access key to unlock the restricted portal.
              </p>
              <p className="text-slate-600 text-[11px] mt-1.5">Codes may be 4–8 characters — leave extra boxes blank.</p>
            </div>

            <form onSubmit={handleSecurityCheck} className="space-y-5" autoComplete="off" data-vault-form="1">
              {/* ───── Honeypot ───── */}
              <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, pointerEvents: 'none', height: 0, width: 0, overflow: 'hidden' }}>
                <label htmlFor="hp_field">Leave this field empty</label>
                <input
                  type="text"
                  id="hp_field"
                  name="hp_field"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
              </div>

              {/* Paste / Caps / Error banners */}
              {pasteWarning && (
                <div className="bg-violet-500/[0.08] border border-violet-500/25 rounded-lg px-3 py-2 flex items-center gap-2">
                  <AlertCircle className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
                  <span className="text-violet-300 text-[11px] leading-snug">
                    Only paste keys from official Starnight communications.
                  </span>
                </div>
              )}
              {capsLockOn && (
                <div className="bg-amber-500/[0.08] border border-amber-500/25 rounded-lg px-3 py-1.5 flex items-center gap-2 justify-center">
                  <Lock className="w-3 h-3 text-amber-300" />
                  <span className="text-amber-300 text-[10px] tracking-wider uppercase">Caps Lock on</span>
                </div>
              )}
              {securityError && (
                <div className="bg-rose-500/[0.08] border border-rose-500/30 rounded-lg px-3 py-2.5 flex items-center gap-2 animate-shake" data-testid="security-error">
                  <AlertCircle className="w-3.5 h-3.5 text-rose-400 flex-shrink-0" />
                  <span className="text-rose-300 text-[12px] leading-snug">{securityError}</span>
                </div>
              )}

              {/* Attempts pips */}
              {securityAttemptsLeft != null && securityAttemptsLeft > 0 && !securityLockout && (
                <div className="flex items-center justify-center gap-1.5">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className={`w-1.5 h-1.5 rounded-full transition-all ${i < securityAttemptsLeft ? 'bg-rose-400 shadow-[0_0_6px_rgba(244,63,94,0.6)]' : 'bg-rose-900/40'}`} />
                  ))}
                  <span className="text-rose-300/70 text-[9px] tracking-[0.2em] uppercase ml-2">
                    {securityAttemptsLeft} attempt{securityAttemptsLeft !== 1 ? 's' : ''} left
                  </span>
                </div>
              )}

              {/* Lockout */}
              {securityLockout && (
                <div className="bg-rose-500/[0.06] border border-rose-500/30 rounded-lg p-3.5 text-center">
                  <Lock className="w-4 h-4 text-rose-400 mx-auto mb-1.5" />
                  <p className="text-rose-300 text-[13px] font-medium">Vault Sealed</p>
                  <p className="text-rose-400/70 text-[11px] mt-0.5">
                    Retry in {Math.max(0, Math.ceil((securityLockout - Date.now()) / 1000))}s
                  </p>
                </div>
              )}

              {/* Progress bar — visual only */}
              <div className="relative h-[3px] rounded-full bg-white/[0.04] overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-rose-500 via-orange-400 to-rose-500 transition-all duration-300 shadow-[0_0_10px_rgba(244,63,94,0.6)]"
                  style={{ width: `${(filledCount / 8) * 100}%` }}
                />
              </div>

              {/* Code chambers — masked by default */}
              <div className="flex justify-center gap-[6px] sm:gap-2" data-testid="security-code-row">
                {codeDigits.map((digit, index) => {
                  const filled = !!digit;
                  return (
                    <div key={index} className="relative">
                      <input
                        id={`code-${index}`}
                        type={revealCode ? 'text' : 'password'}
                        value={digit}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleCodeKeyDown(index, e)}
                        onPaste={handleCodePaste}
                        className={`w-[38px] h-[54px] sm:w-11 sm:h-14 bg-black/50 border rounded-xl text-center text-xl font-mono uppercase focus:outline-none transition-all disabled:opacity-40 shadow-inner shadow-black/50 caret-transparent
                          ${filled
                            ? 'border-rose-400/60 text-rose-100 bg-rose-500/[0.06] shadow-[0_0_14px_rgba(244,63,94,0.20)]'
                            : 'border-rose-500/20 text-white'}
                          focus:border-rose-400 focus:ring-2 focus:ring-rose-500/30 focus:bg-black/70`}
                        maxLength={1}
                        autoComplete="off"
                        inputMode="text"
                        spellCheck={false}
                        aria-label={`Access key character ${index + 1}`}
                        disabled={!!securityLockout || securityLoading}
                        data-testid={`security-code-${index}`}
                      />
                      {/* Subtle chamber divider indicator */}
                      <span
                        className={`pointer-events-none absolute left-1/2 -translate-x-1/2 -bottom-1.5 h-[3px] w-[3px] rounded-full transition-all ${
                          filled ? 'bg-rose-400 shadow-[0_0_6px_rgba(244,63,94,0.7)]' : 'bg-white/10'
                        }`}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Hidden mirror input for form value */}
              <input type="hidden" value={securityCode} data-testid="security-code-input" />

              {/* Reveal toggle + status row */}
              <div className="flex items-center justify-between text-[10px] tracking-[0.2em] uppercase text-slate-500">
                <button
                  type="button"
                  onClick={() => setRevealCode(v => !v)}
                  className="inline-flex items-center gap-1.5 text-slate-400 hover:text-rose-300 transition-colors group"
                  data-testid="security-reveal-toggle"
                  aria-pressed={revealCode}
                >
                  {revealCode ? (
                    <>
                      <EyeOff className="w-3 h-3" />
                      <span>Hide key</span>
                    </>
                  ) : (
                    <>
                      <Eye className="w-3 h-3" />
                      <span>Peek</span>
                    </>
                  )}
                </button>
                <span className="inline-flex items-center gap-1.5 text-emerald-400/70">
                  <ScanLine className="w-3 h-3" />
                  <span>Encrypted entry</span>
                </span>
              </div>

              {/* Idle countdown */}
              {codeDigits.some(d => d) && idleSecondsLeft < 30 && !securityLockout && (
                <p className="text-center text-rose-400/60 text-[10px] tracking-[0.2em] uppercase animate-pulse">
                  Auto-clearing in {idleSecondsLeft}s
                </p>
              )}

              <Button
                type="submit"
                disabled={securityLoading || !!securityLockout || codeDigits.join('').length < 8}
                data-testid="security-submit-btn"
                className="w-full bg-gradient-to-r from-rose-500 via-rose-500 to-orange-500 hover:from-rose-400 hover:to-orange-400 text-white py-5 text-[14px] font-semibold rounded-xl shadow-lg shadow-rose-500/30 transition-all hover:shadow-rose-500/50 hover:translate-y-[-1px] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 ring-1 ring-white/10"
              >
                {securityLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Authenticating…
                  </span>
                ) : (
                  <>
                    <KeyRound className="w-4 h-4 mr-2" />
                    Unlock Vault
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Trust rail */}
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-[9px] tracking-[0.22em] uppercase">
            <span className="flex items-center gap-1 text-emerald-400/80">
              <Lock className="w-2.5 h-2.5" />
              TLS Secured
            </span>
            <span className="text-slate-700">·</span>
            <span className="flex items-center gap-1 text-slate-500">
              <Fingerprint className="w-2.5 h-2.5" />
              <span className="font-mono">SID {sessionFp}</span>
            </span>
            <span className="text-slate-700">·</span>
            <span className="flex items-center gap-1 text-rose-300/70">
              <Shield className="w-2.5 h-2.5" />
              Bot-Guard
            </span>
            <span className="text-slate-700">·</span>
            <span className="flex items-center gap-1 text-slate-500 font-mono tabular-nums">
              <Clock className="w-2.5 h-2.5" />
              {formatTime(currentTime)}
            </span>
          </div>

          <div className="mt-5 text-center">
            <a href="/" className="inline-flex items-center gap-1.5 text-slate-500 hover:text-rose-300 transition-colors text-[13px]">
              ← Return to main website
            </a>
          </div>

          <p className="mt-4 text-center text-slate-700 text-[9px] tracking-[0.22em] uppercase">
            Unauthorised access attempts are logged &amp; monitored
          </p>
        </div>
      </div>
    );
  }

  // 2FA Verification Screen — Refreshed with multi-layer protection
  if (requires2FA) {
    const codeStr = twoFACode.join('');
    const isFull = codeStr.length === 6;
    const totpPct = (twoFATotpSec / 30) * 100;
    const totpUrgent = twoFATotpSec <= 5;

    return (
      <div className="min-h-screen bg-[#070710] flex items-center justify-center p-6 relative overflow-hidden" data-testid="twofa-screen">
        {/* Ambient Vault backdrop */}
        <div aria-hidden className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -left-16 w-96 h-96 rounded-full blur-3xl bg-cyan-500/15" />
          <div className="absolute -bottom-40 -right-16 w-[520px] h-[520px] rounded-full blur-3xl bg-violet-600/12" />
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)', backgroundSize: '28px 28px' }} />
        </div>

        <div className="relative z-10 w-full max-w-md">
          <style>{`
            @keyframes twofa-shine {
              0% { background-position: -200% 0; }
              100% { background-position: 200% 0; }
            }
            @keyframes shake-x {
              10%, 90% { transform: translateX(-1px); }
              20%, 80% { transform: translateX(2px); }
              30%, 50%, 70% { transform: translateX(-4px); }
              40%, 60% { transform: translateX(4px); }
            }
            .animate-shake-x { animation: shake-x 0.45s cubic-bezier(.36,.07,.19,.97); }
          `}</style>

          <div className={`relative ${twoFAShake ? 'animate-shake-x' : ''}`}>
            {/* Conic gradient ring */}
            <div
              aria-hidden
              className="absolute -inset-[1px] rounded-[26px] opacity-70 blur-[1px]"
              style={{
                background: twoFASuccess
                  ? 'conic-gradient(from 90deg, transparent 0deg, #10B981 60deg, transparent 140deg, #22D3EE 220deg, transparent 300deg)'
                  : twoFALocked
                    ? 'conic-gradient(from 90deg, transparent 0deg, #F43F5E 60deg, transparent 140deg, #F97316 220deg, transparent 300deg)'
                    : 'conic-gradient(from 90deg, transparent 0deg, #00E5FF 60deg, transparent 140deg, #8B5CF6 220deg, transparent 300deg)',
                animation: 'spin 8s linear infinite',
              }}
            />

            <div className="relative bg-gradient-to-b from-slate-950/95 via-slate-950/98 to-slate-950 backdrop-blur-2xl rounded-[24px] p-8 ring-1 ring-white/5 shadow-[0_30px_120px_-20px_rgba(0,229,255,0.35)]">
              {/* Header */}
              <div className="flex flex-col items-center text-center mb-6">
                <div className="relative mb-4">
                  <div className={`absolute inset-0 rounded-2xl blur-2xl scale-125 ${twoFASuccess ? 'bg-emerald-500/40' : twoFALocked ? 'bg-rose-500/40' : 'bg-gradient-to-br from-cyan-500/40 to-violet-500/40'}`} />
                  <div className={`relative w-16 h-16 rounded-2xl p-[2px] shadow-2xl ${twoFASuccess ? 'bg-gradient-to-br from-emerald-500 to-teal-600 shadow-emerald-500/30' : twoFALocked ? 'bg-gradient-to-br from-rose-500 to-orange-600 shadow-rose-500/30' : 'bg-gradient-to-br from-cyan-500 to-blue-600 shadow-cyan-500/30'}`}>
                    <div className="w-full h-full rounded-[14px] bg-slate-950 flex items-center justify-center">
                      {twoFASuccess ? (
                        <svg className="w-7 h-7 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                      ) : twoFALocked ? (
                        <Lock className="w-7 h-7 text-rose-400" strokeWidth={2.2} />
                      ) : (
                        <Fingerprint className="w-7 h-7 text-white" strokeWidth={2.2} />
                      )}
                    </div>
                  </div>
                  {/* Floating crest */}
                  <div className={`absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center ring-[2.5px] ring-slate-950 shadow-lg ${twoFASuccess ? 'bg-gradient-to-br from-emerald-400 to-teal-500 shadow-emerald-500/40' : twoFALocked ? 'bg-gradient-to-br from-rose-500 to-orange-500 shadow-rose-500/40' : 'bg-gradient-to-br from-amber-400 to-orange-500 shadow-amber-500/40'}`}>
                    {twoFASuccess ? (
                      <Shield className="w-3 h-3 text-white" strokeWidth={2.4} />
                    ) : twoFALocked ? (
                      <Lock className="w-3 h-3 text-white" strokeWidth={2.4} />
                    ) : (
                      <ShieldAlert className="w-3 h-3 text-white" strokeWidth={2.4} />
                    )}
                  </div>
                </div>
                <p className={`text-[10px] font-bold uppercase tracking-[0.28em] mb-1.5 flex items-center gap-1.5 ${twoFASuccess ? 'text-emerald-400/80' : twoFALocked ? 'text-rose-400/80' : 'text-cyan-400/80'}`}>
                  <Sparkles className="w-3 h-3" /> {twoFASuccess ? 'Authenticated' : twoFALocked ? 'Verification Locked' : 'Two-Factor Gate'}
                </p>
                <h1 className="text-2xl font-bold text-white tracking-tight mb-1.5">
                  {twoFASuccess ? 'Access Granted' : 'Two-Factor Authentication'}
                </h1>
                <p className="text-slate-400 text-[13px] max-w-xs leading-relaxed">
                  Enter the 6-digit code from your authenticator app.
                </p>
                {twoFAUsername2 && (
                  <p className="text-slate-500 text-[11px] mt-2">
                    Verifying as <span className="text-cyan-400 font-medium">@{twoFAUsername2}</span>
                  </p>
                )}
              </div>

              {processing2FA ? (
                <div className="text-center py-8">
                  <div className="relative w-14 h-14 mx-auto mb-4">
                    <div className="absolute inset-0 border-4 border-cyan-500/20 rounded-full" />
                    <div className="absolute inset-0 border-4 border-transparent border-t-cyan-400 rounded-full animate-spin" />
                  </div>
                  <p className="text-slate-300 text-sm font-medium">Verifying your code…</p>
                  <p className="text-slate-500 text-xs mt-1">Please wait</p>
                </div>
              ) : (
                <form onSubmit={handle2FAVerification} className="space-y-4" autoComplete="off">
                  {/* Honeypot */}
                  <input type="text" tabIndex={-1} autoComplete="off" name="email_confirm"
                    value={twoFAHoneypot} onChange={(e) => setTwoFAHoneypot(e.target.value)}
                    style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }} />

                  {/* TOTP window indicator */}
                  <div className="flex items-center justify-between text-[10px]">
                    <div className="flex items-center gap-1.5 font-bold uppercase tracking-[0.16em] text-slate-500">
                      <KeyRound className="w-3 h-3" /> Authenticator Code
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="relative w-5 h-5">
                        <svg className="w-5 h-5 -rotate-90" viewBox="0 0 20 20">
                          <circle cx="10" cy="10" r="8" fill="none" stroke="rgba(148,163,184,0.2)" strokeWidth="2" />
                          <circle cx="10" cy="10" r="8" fill="none"
                            stroke={totpUrgent ? '#fb923c' : '#22d3ee'}
                            strokeWidth="2" strokeLinecap="round"
                            strokeDasharray={`${(totpPct / 100) * 50.27} 50.27`}
                            className="transition-all duration-1000" />
                        </svg>
                      </div>
                      <span className={`font-mono font-bold tabular-nums ${totpUrgent ? 'text-orange-400' : 'text-cyan-400'}`}>
                        {twoFATotpSec}s
                      </span>
                    </div>
                  </div>

                  {/* OTP digit boxes */}
                  <div className="flex justify-center gap-2" onPaste={handle2FAPaste}>
                    {twoFACode.map((digit, index) => {
                      const filled = !!digit;
                      return (
                        <input
                          key={index}
                          id={`2fa-${index}`}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={digit}
                          onChange={(e) => handle2FACodeChange(index, e.target.value)}
                          onKeyDown={(e) => handle2FAKeyDown(index, e)}
                          disabled={twoFALocked || twoFASuccess}
                          className={`w-12 h-14 sm:w-[52px] sm:h-[60px] rounded-xl text-white text-center text-2xl font-bold font-mono caret-cyan-400 focus:outline-none transition-all duration-200 ${
                            twoFASuccess
                              ? 'bg-emerald-500/15 ring-2 ring-emerald-400/60 shadow-[0_0_18px_rgba(52,211,153,0.4)]'
                              : twoFALocked
                                ? 'bg-rose-500/10 ring-2 ring-rose-500/40 opacity-60 cursor-not-allowed'
                                : filled
                                  ? 'bg-cyan-500/10 ring-2 ring-cyan-400/60 shadow-[0_0_14px_rgba(34,211,238,0.25)]'
                                  : 'bg-slate-900/60 ring-1 ring-slate-800 hover:ring-slate-700 focus:ring-2 focus:ring-cyan-500/50 focus:bg-slate-900'
                          }`}
                          maxLength={1}
                          autoComplete="off"
                          autoFocus={index === 0}
                          data-testid={`2fa-code-${index}`}
                        />
                      );
                    })}
                  </div>

                  {/* Status row */}
                  <div className="flex items-center justify-between text-[10px]">
                    <div className="flex items-center gap-1.5 text-slate-500">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      <span className="uppercase tracking-wider">Encrypted · TLS</span>
                    </div>
                    <div className="text-slate-500">
                      Auto-clears in <span className={`font-mono tabular-nums ${twoFAIdleSec <= 10 ? 'text-orange-400' : 'text-slate-300'}`}>{twoFAIdleSec}s</span>
                    </div>
                  </div>

                  {/* Attempts indicator */}
                  {twoFAAttempts > 0 && !twoFALocked && (
                    <div className="flex items-center justify-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className={`w-2 h-1.5 rounded-full transition-all ${
                          i < twoFAAttempts ? 'bg-rose-500/80' : 'bg-slate-700/60'
                        }`} />
                      ))}
                      <span className="ml-2 text-[10px] text-slate-500 uppercase tracking-wider">
                        {5 - twoFAAttempts} left
                      </span>
                    </div>
                  )}

                  {error && (
                    <div className="flex items-start gap-2.5 rounded-xl bg-rose-500/10 ring-1 ring-rose-500/30 px-3.5 py-2.5" data-testid="twofa-error">
                      <AlertCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                      <span className="text-[12px] leading-relaxed text-rose-200 font-medium">{error}</span>
                    </div>
                  )}

                  {/* Paste warning */}
                  {twoFAUsedPaste && !twoFALocked && (
                    <div className="rounded-xl bg-violet-500/[0.06] ring-1 ring-violet-500/20 px-3.5 py-2.5 flex items-center gap-2">
                      <AlertCircle className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
                      <span className="text-violet-300/90 text-[11px]">Pasted code detected — make sure it's still valid (codes refresh every 30s).</span>
                    </div>
                  )}

                  {/* Lock notice */}
                  {twoFALocked && (
                    <div className="rounded-xl bg-rose-500/10 ring-1 ring-rose-500/30 p-3 text-center">
                      <Lock className="w-5 h-5 text-rose-400 mx-auto mb-1.5" />
                      <p className="text-rose-300 text-xs font-semibold">Verification locked</p>
                      <p className="text-rose-400/70 text-[11px] mt-0.5">Cancel and sign in again to retry</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={!isFull || twoFALocked || twoFASuccess}
                    data-testid="verify-2fa-btn"
                    className="relative w-full py-3.5 rounded-xl bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-600 bg-[length:200%_100%] hover:bg-[position:100%_0] text-white font-semibold text-[14px] tracking-wide shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:shadow-cyan-500/30 flex items-center justify-center gap-2 overflow-hidden group"
                  >
                    <span aria-hidden className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent bg-[length:200%_100%] opacity-0 group-hover:opacity-100" style={{ animation: 'twofa-shine 2s linear infinite' }} />
                    {twoFASuccess ? (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                        Authenticated
                      </>
                    ) : (
                      <>
                        <Shield className="w-4 h-4" />
                        Verify &amp; Sign In
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={cancel2FA}
                    className="w-full text-slate-500 hover:text-slate-300 hover:bg-slate-900/40 py-2.5 text-[12px] font-medium rounded-lg transition-all"
                  >
                    ← Cancel and go back
                  </button>
                </form>
              )}

              {/* Footer */}
              <div className="mt-6 pt-5 border-t border-slate-800/60 flex items-center justify-between text-[10px] font-medium text-slate-500 uppercase tracking-[0.14em]">
                <span className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.9)] animate-pulse" />
                  Bot-Guard Active
                </span>
                <span className="font-mono normal-case tracking-normal text-slate-600">SID {sessionFp}</span>
              </div>
            </div>
          </div>

          {/* Help text */}
          <p className="text-center text-slate-600 text-[11px] mt-5 leading-relaxed">
            Lost access to your authenticator?<br />
            Contact a Super Admin to reset your 2FA.
          </p>
        </div>
      </div>
    );
  }

  // Force Password Change Screen
  if (mustChangePassword) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-8">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
        <div className="relative z-10 w-full max-w-md">
          <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-800/60 rounded-2xl p-8 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-violet-500/20 to-red-500/20 border border-violet-500/30 rounded-2xl flex items-center justify-center">
                <ShieldAlert className="w-7 h-7 text-violet-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Password Change Required</h2>
              <p className="text-sm text-slate-400 mt-2">A security policy requires you to change your password before continuing.</p>
            </div>
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-4 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                <span className="text-red-400 text-sm">{error}</span>
              </div>
            )}
            <form onSubmit={handleForcePasswordChange} className="space-y-4" data-testid="force-change-form">
              <div>
                <label className="block text-xs text-slate-400 mb-1.5 uppercase tracking-wider font-medium">New Password</label>
                <input type="password" value={newPasswordInput} onChange={(e) => setNewPasswordInput(e.target.value)}
                  data-testid="new-password-input"
                  className="w-full px-4 py-3 bg-black/40 border border-slate-700 rounded-xl text-white placeholder-slate-600 focus:border-violet-500/40 focus:outline-none text-sm"
                  placeholder="Min 8 characters" autoFocus />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1.5 uppercase tracking-wider font-medium">Confirm Password</label>
                <input type="password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)}
                  data-testid="confirm-new-password-input"
                  className="w-full px-4 py-3 bg-black/40 border border-slate-700 rounded-xl text-white placeholder-slate-600 focus:border-violet-500/40 focus:outline-none text-sm"
                  placeholder="Re-enter password" />
              </div>
              <Button type="submit" disabled={changingPassword || newPasswordInput.length < 8 || newPasswordInput !== confirmNewPassword}
                data-testid="change-password-btn"
                className="w-full bg-gradient-to-r from-violet-500 to-orange-600 hover:from-violet-600 hover:to-orange-700 text-white py-5 text-base font-semibold rounded-xl disabled:opacity-50">
                {changingPassword ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Changing...
                  </span>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" /> Set New Password
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Main Login Screen (after security check)
  return (
    <div className="min-h-screen bg-[#060610] flex" data-testid="staff-login-page">
      {/* Left Panel — Premier branding */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] bg-cyan-500/[0.04] rounded-full blur-[120px]" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-blue-500/[0.03] rounded-full blur-[100px]" />
        </div>
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets-39nsmqrw.emergentagent.net/job_209c8813-566e-47da-85c4-1d1fadce8833/artifacts/rzf7k7uf_starnight101.png" alt="Starnight Holdings" className="h-9 w-auto object-contain opacity-80" />
          </div>
          <div className="space-y-8 max-w-lg">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="w-8 h-px bg-cyan-500/50" />
                <span className="text-cyan-400 text-xs tracking-[0.2em] uppercase font-medium">Staff Portal</span>
              </div>
              <h1 className="text-5xl font-bold text-white leading-[1.1] tracking-tight">Welcome<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-300">Back</span></h1>
              <p className="text-slate-500 text-base mt-4 leading-relaxed max-w-sm">Access your workspace. Manage operations. Lead with confidence.</p>
            </div>
            <div className="space-y-3">
              {[{ icon: Shield, label: 'Security gateway verified', c: 'emerald' }, { icon: Lock, label: 'End-to-end encrypted session', c: 'cyan' }, { icon: Fingerprint, label: 'Biometric-ready authentication', c: 'blue' }, { icon: ShieldAlert, label: 'Real-time threat monitoring', c: 'purple' }].map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg bg-${f.c}-500/10 border border-${f.c}-500/20 flex items-center justify-center`}><f.icon className={`w-4 h-4 text-${f.c}-400`} /></div>
                  <span className="text-slate-400 text-sm">{f.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between text-slate-700 text-xs">
            <div className="flex items-center gap-3"><span className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" /> Secure</span><span className="text-slate-800">|</span><span>{formatDate(currentTime)}</span></div>
            <span className="font-mono text-slate-600">{formatTime(currentTime)}</span>
          </div>
        </div>
      </div>
      <div className="w-full lg:w-[45%] flex items-center justify-center p-6 lg:p-12 relative overflow-hidden">
        {/* Ambient Vault backdrop */}
        <div aria-hidden className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -left-16 w-96 h-96 rounded-full blur-3xl bg-cyan-500/15" />
          <div className="absolute -bottom-40 -right-16 w-[520px] h-[520px] rounded-full blur-3xl bg-violet-600/10" />
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)', backgroundSize: '28px 28px' }} />
        </div>

        <div className="relative z-10 w-full max-w-md">
          <style>{`
            @keyframes login-shake {
              10%, 90% { transform: translateX(-1px); }
              20%, 80% { transform: translateX(2px); }
              30%, 50%, 70% { transform: translateX(-4px); }
              40%, 60% { transform: translateX(4px); }
            }
            @keyframes login-shine {
              0% { background-position: -200% 0; }
              100% { background-position: 200% 0; }
            }
          `}</style>

          <div className="lg:hidden flex items-center justify-center gap-3 mb-6">
            <img src="https://customer-assets-39nsmqrw.emergentagent.net/job_209c8813-566e-47da-85c4-1d1fadce8833/artifacts/rzf7k7uf_starnight101.png" alt="Starnight" className="h-7 w-auto opacity-80" />
            <div className="flex items-center gap-1.5 ml-2 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20"><div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" /><span className="text-emerald-400 text-[10px] font-medium">Secured</span></div>
          </div>

          <div className={`relative ${error ? 'animate-[login-shake_0.45s_cubic-bezier(.36,.07,.19,.97)]' : ''}`}>
            {/* Conic gradient ring around card */}
            <div
              aria-hidden
              className="absolute -inset-[1px] rounded-[26px] opacity-70 blur-[1px]"
              style={{
                background: 'conic-gradient(from 90deg, transparent 0deg, #00E5FF 60deg, transparent 140deg, #8B5CF6 220deg, transparent 300deg)',
                animation: 'spin 8s linear infinite',
              }}
            />

            <div className="relative bg-gradient-to-b from-slate-950/95 via-slate-950/98 to-slate-950 backdrop-blur-2xl rounded-[24px] p-8 ring-1 ring-white/5 shadow-[0_30px_120px_-20px_rgba(0,229,255,0.35)]" data-testid="login-form-card">
              {/* Header */}
              <div className="flex flex-col items-center text-center mb-6">
                <div className="relative mb-4">
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500/40 to-blue-500/40 blur-2xl scale-125" />
                  <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 p-[2px] shadow-2xl shadow-cyan-500/30">
                    <div className="w-full h-full rounded-[14px] bg-slate-950 flex items-center justify-center">
                      <User className="w-7 h-7 text-white" strokeWidth={2.2} />
                    </div>
                  </div>
                  {/* Floating shield crest */}
                  <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center ring-[2.5px] ring-slate-950 shadow-lg shadow-emerald-500/40">
                    <Shield className="w-3 h-3 text-white" strokeWidth={2.4} />
                  </div>
                </div>
                <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-cyan-400/80 mb-1.5 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" /> Zero-Trust Portal
                </p>
                <h1 className="text-2xl font-bold text-white tracking-tight mb-1.5">Sign In</h1>
                <p className="text-slate-400 text-[13px] max-w-xs leading-relaxed">
                  Verify your identity to unlock the Staff Hub for this session.
                </p>
              </div>

              <form onSubmit={handleLogin} className="space-y-4" autoComplete="off">
                {/* Honeypot */}
                <input type="text" tabIndex={-1} autoComplete="off" name="website" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }} />

                <div>
                  <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-2">
                    <User className="w-3 h-3" /> Username
                  </label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    autoComplete="username"
                    spellCheck={false}
                    data-testid="username-input"
                    placeholder="your.username"
                    className="w-full bg-slate-900/60 ring-1 ring-slate-800 hover:ring-slate-700 focus:ring-cyan-500/50 focus:bg-slate-900 rounded-xl px-4 py-3.5 text-white text-[14px] font-medium placeholder-slate-700 outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-2">
                    <KeyRound className="w-3 h-3" /> Password
                  </label>
                  <div className={`relative group ${error ? 'ring-1 ring-rose-500/40 rounded-xl' : ''}`}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={(e) => setCapsLockOn(e.getModifierState && e.getModifierState('CapsLock'))}
                      required
                      autoComplete="current-password"
                      data-testid="password-input"
                      placeholder="••••••••••••"
                      className="w-full bg-slate-900/60 ring-1 ring-slate-800 hover:ring-slate-700 focus:ring-cyan-500/50 focus:bg-slate-900 rounded-xl pl-4 pr-11 py-3.5 text-white text-[15px] tracking-widest font-medium placeholder-slate-700 outline-none transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                      title={showPassword ? 'Hide' : 'Reveal'}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-500 hover:text-cyan-300 hover:bg-slate-800/60 transition-all"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {capsLockOn && (
                    <p className="mt-1.5 text-[10px] text-amber-400 font-medium flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> Caps Lock is ON
                    </p>
                  )}
                </div>

                {error && (
                  <div className="flex items-start gap-2.5 rounded-xl bg-rose-500/10 ring-1 ring-rose-500/30 px-3.5 py-2.5" data-testid="login-error">
                    <AlertCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                    <span className="text-[12px] leading-relaxed text-rose-200 font-medium">{error}</span>
                  </div>
                )}

                <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl bg-violet-500/5 ring-1 ring-violet-500/15">
                  <ShieldAlert className="w-3.5 h-3.5 text-violet-400/70 flex-shrink-0" />
                  <p className="text-violet-400/80 text-[11px] leading-relaxed">Account locks after 5 failed attempts. All activity is monitored.</p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-btn"
                  className="relative w-full py-3.5 rounded-xl bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-600 bg-[length:200%_100%] hover:bg-[position:100%_0] text-white font-semibold text-[14px] tracking-wide shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:shadow-cyan-500/30 flex items-center justify-center gap-2 overflow-hidden group"
                >
                  <span aria-hidden className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent bg-[length:200%_100%] opacity-0 group-hover:opacity-100" style={{ animation: 'login-shine 2s linear infinite' }} />
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Authenticating…
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4" />
                      Sign In
                    </>
                  )}
                </button>
              </form>

              {/* Footer */}
              <div className="mt-6 pt-5 border-t border-slate-800/60 flex items-center justify-between text-[10px] font-medium text-slate-500 uppercase tracking-[0.14em]">
                <span className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.9)] animate-pulse" />
                  Zero-Trust Session
                </span>
                <span className="font-mono normal-case tracking-normal text-slate-600">SID {sessionFp}</span>
              </div>
            </div>
          </div>

          <div className="mt-6 text-center"><a href="/" className="text-slate-600 hover:text-slate-400 transition-colors text-xs tracking-wide">← Return to main website</a></div>
          <p className="mt-3 text-center text-slate-800 text-[10px]">Starnight Holdings Ltd. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default StaffLogin;
